import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ITableColumn } from "../../../../components";
import DataTable from "../../../../components/components/Table/DataTable";
import { PolicyCoverageReadDto } from "../../../model/PolicyCoverageReadDto";
import { CustomerPolicyDetailLabels as cstPolicyCoverageLabel } from '../../../../shared/util/label';
import { PolicySummaryErrorMessages } from "../../../../shared/util/constant";
import CustomerBasicCoverageForm from "./CustomerBasicCoverageForm";
import { useConfirmationModalContext } from "../../../../shared/components/ModalConfirmationContext";
import AppContext from "../../../../shared/components/AppContext";
import { RootState } from "../../../../shared/store";
import CustomerBasicCoverageService from "./CustomerBasicCoverageService";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { toast } from "react-toastify";
import { uuid } from "../../../../shared/util/uuid";
import { PolicyLOBReadDto } from "../../../model/PolicyLOBReadDto";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { SelectOption } from "../../../../shared/components/input/SelectOption";
import LookupService from "../../../../Services/LookupService";
import * as constant from '../../../../shared/util/constant';
import { PolicyLocationReadDto } from "../../../model/PolicyLocationReadDto";
import { updateLookupData } from "../../../customer-slice/Customer-lookup-slice";
import './CustomerEditPolicyBasicCoverageGrid.scss';

export interface ICustomerEditPolicyBasicCoverageGrid {
  customerId?: string;
  policyId?: string;
  policyEventId?: string;
  isCoverages?: boolean;
  validationErrors?: any;
  tabIndex?: number;
}

export interface CoverageState {
  isEditing: boolean;
  isAddingNewCoverage: boolean;
}

const initialCoverageState: CoverageState = {
  isEditing: false,
  isAddingNewCoverage: false,
};

const CustomerEditPolicyBasicCoverageGrid: React.FunctionComponent<ICustomerEditPolicyBasicCoverageGrid> = (props) => {
  let tbIndex = props.tabIndex;
  const { customerId, policyId, policyEventId, isCoverages = true, validationErrors } = props;
  const dispatch = useDispatch();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [isFormSaved, setIsFormSaved] = useState(true);
  const [currentCoverageData, setCurrentCoverageData] = useState<PolicyCoverageReadDto | null>(null);
  const modalContext = useConfirmationModalContext();
  const [policyCoverages, setPolicyCoverages] = useState<PolicyCoverageReadDto[]>([]);
  const [lobs, setLobs] = useState<PolicyLOBReadDto[]>([]);
  const customerDetail = useSelector((state: RootState) => state?.customerSlice);
  const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, policyEventId);
  const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
  const selectedLocationDetails = customerDetail.selectedLocationDetails;
  const [coverageState, setCoverageState] = useState<CoverageState>(initialCoverageState);
  const customerBasicCoverageService = new CustomerBasicCoverageService();
  const [deductibleTypes, setDeductibleTypes] = useState<SelectOption[]>([]);
  const deductibleTypesList = useSelector((state: RootState) => state?.LookupState.deductibleTypes);
  const [polLocations, setPolLocations] = useState<PolicyLocationReadDto[]>([]);

  const totalPremium = policyCoverages.reduce((acc, row) => acc + (row.fullTermPremium || 0.00), 0.00);
  useEffect(() => {
      // Fetch coverage data from the customer slice
      const lobArray = policyDetails?.policyLineOfBusinesses || [];
      setLobs(lobArray);
      const policyLocations = lobArray.flatMap(lob => lob.policyLocations || [])
      .filter(lob => lob.polEventId === policyEventId);
      setPolLocations(policyLocations);

      // Extract coverages that have isCoverage=true
      const coverages = lobArray
        .flatMap(lob => lob.policyCoverages || [])
        .filter(coverage => coverage.isCoverage === isCoverages)
        .filter(coverage => coverage.attachTypeId === constant.CS2_AttachTypes.LOCATION && coverage.attachId === selectedLocationDetails?.id);

      setPolicyCoverages(coverages);

  }, [policyId, policyEventId, isCoverages, selectedLocationDetails]);

  useEffect(() => {
    loadDeductibleTypes();
  }, [policyId]);

  const loadDeductibleTypes = async () => {
      // Skip loading if we already have data
      if (deductibleTypesList && deductibleTypesList.length > 0) {
        setDeductibleTypes([...deductibleTypesList]);
        return;
      }
      const deductibleTypesResponse = await customerBasicCoverageService.getDeductibleTypes();
      setDeductibleTypes(deductibleTypesResponse);
      dispatch(updateLookupData({ data: deductibleTypesResponse, type: constant.SlicerLookUpConstant.DEDUCTIBLE_TYPES }));
  };

  const onRowClick = (rowData: any, column: ITableColumn) => {
    // Don't allow clicking while already editing a different row
    if (coverageState.isEditing && expandedRowId !== rowData.id) {
      return;
    }

    // If clicking the same row that's expanded, collapse it
    if (expandedRowId === rowData.id) {
      setExpandedRowId(null);
      setCurrentCoverageData(null);
      setPolicyCoverages([...policyCoverages]);
      setCoverageState({ isEditing: false, isAddingNewCoverage: false });
    } else {
      // Otherwise, expand the clicked row
      setExpandedRowId(rowData.id);
      setCurrentCoverageData(rowData);
      setCoverageState({ isEditing: true, isAddingNewCoverage: false });
    }
  };

  const onCancelClick = async () => {    
    setExpandedRowId(null);
    setCurrentCoverageData(null);
    setIsFormSaved(true);
    setCoverageState({ isEditing: false, isAddingNewCoverage: false });
  };

const onRowDeleteClick = async (rowData: any, column: ITableColumn) => {
    try {

        const result = await modalContext.showConfirmation("Are you sure you want to delete this coverage?");
        if (!result) {
            return;
        }

        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        
        if (updatedPolicyDetails?.policyLineOfBusinesses) {
            const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map(lob => {
                if (policyEventId && lob.polEventId !== policyEventId) {
                    return lob;
                }
                
                if (lob.policyCoverages?.length > 0) {
                    return {
                        ...lob,
                        policyCoverages: lob.policyCoverages.filter(coverage => coverage.id !== rowData.id)
                    };
                }
                
                return lob;
            });
            
            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;
            
            setPolicyCoverages(prevCoverages => 
                prevCoverages.filter(coverage => coverage.id !== rowData.id)
            );
    
            dispatch(updateSelectedPolicyDetails({ 
                policyId: policyId, 
                polEventId: policyEventId, 
                policyDetails: updatedPolicyDetails 
            }));

            setExpandedRowId(null);
            setCurrentCoverageData(null);
            setCoverageState({ isEditing: false, isAddingNewCoverage: false });
            setIsFormSaved(true);
        }
    } catch (ex) {
        console.error("Error deleting coverage", ex);
        // Even on error, ensure UI state is reset
        setCoverageState({ isEditing: false, isAddingNewCoverage: false });
        setIsFormSaved(true);
    }
};

  // Add handler for Add New Coverage click - similar to CustomerLoss
  const onAddNewCoverageClick = (e) => {
    if (coverageState.isAddingNewCoverage || coverageState.isEditing) {
      return;
    }
    const newCoverage: PolicyCoverageReadDto = {
      id: uuid(),
      coverageCode: '',
      limit1: null,
      limit2: null,
      fullTermPremium: null,
      deductibleType1: null,
      deductibleType2: null,
      deductibleType3: null,
      deductible1: null,
      deductible2: null,
      deductible3: null,
      isCoverage:  isCoverages ? true : false, // Set isCoverage
      attachTypeId: constant.CS2_AttachTypes.LOCATION,
      attachId: selectedLocationDetails?.id || null,
      effDate: policyDetails?.polEffDate,
      expDate: policyDetails?.polExpDate,
      lobId: lobs[0]?.id,
      polId: policyDetails?.id,
      polEventId: lobs[0]?.polEventId,
      slobOption1: null,
      slobOption2: null,
      transactionPremium: null,
      statusTypeId: "022FC2A1-EC80-4CDF-9002-F22FF1F3F291",
      policyAutoCoverageOption: null,
      typeOfCode: null
    };

    // Add the new coverage to the list at the top for display
    setPolicyCoverages([newCoverage, ...policyCoverages]);
    setCurrentCoverageData(newCoverage);
    setCoverageState({ isEditing: false, isAddingNewCoverage: true });
    setExpandedRowId(newCoverage.id); // Set the expanded row to the new row's ID
  };

  const buttonLabel = isCoverages ? cstPolicyCoverageLabel.ADD_CUSTOMER_COVERAGE : cstPolicyCoverageLabel.ADD_CUSTOMER_ENDORSEMENT;
  let AddNewCoverageButton = <></>;
  if (!coverageState.isAddingNewCoverage && !coverageState.isEditing) {
    AddNewCoverageButton = (
      <Button
        className="mb-2"
        onClick={(e) => onAddNewCoverageClick(e)}
        id={isCoverages ? "btnCoverageAdd" : "btnEndorsementAdd"}
        disabled={polLocations.length === 0}
        tabIndex={tbIndex++}
      >
        <i className="bi bi-plus-circle me-2"></i>
        {buttonLabel}
      </Button>
    );
  }

  // Coverage form component
  const coverageFormView = (
    <CustomerBasicCoverageForm
      customerId={customerId}
      polEventId={policyEventId}
      polId={policyId}
      coverageId={currentCoverageData?.id}
      customerBasicCoverage={currentCoverageData}
      setIsFormSaved={setIsFormSaved}
      isCoverage={isCoverages}
      deductibleTypesOptions={deductibleTypes}
      formValidation={validationErrors}
      lobCodeId={policyDetails?.policyLineOfBusinesses?.find(lob => lob.polEventId === policyEventId)?.lobCodeId}
      tabIndex={tbIndex}
    />
  );

  // Render the form row for the expanded row
  const renderRow = (coverage, index) => {
    return (
    <React.Fragment key={coverage.id || index}>
      {((coverageState.isEditing || coverageState.isAddingNewCoverage) && 
        currentCoverageData && currentCoverageData.id === coverage.id) && (
        <tr>
          <td colSpan={tableColumns.length}>
            <div className="customer-coverage-form-wrapper">
              {coverageFormView}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
    );
  };

  // Get columns from service with row click handler and cancel handler
  const tableColumns: ITableColumn[] = customerBasicCoverageService.getCoverageTableColumns(
    onRowClick, 
    onCancelClick, 
    onRowDeleteClick,
    deductibleTypes, 
    isCoverages,
    policyDetails?.policyLineOfBusinesses?.find(lob => lob.polEventId === policyEventId)?.lobCodeId,
    tbIndex
  );

  return (
    <div className="customer-basic-coverage-table">
      <div className="coverage-actions">
        {AddNewCoverageButton}
      </div>

      <DataTable
        columns={tableColumns}
        data={policyCoverages}
        keyColumnName="id"
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        renderRow={renderRow}
        isFormSaved={isFormSaved}
        tableClassName="table-striped table-hover table-sm"
        theadClassName="tblHeader"
      />

      <div className="total_premium">
        <span>Total:${totalPremium.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default CustomerEditPolicyBasicCoverageGrid;